import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { r2Storage } from '../services/r2Storage';
import { analytics } from '../services/analytics';
import { subscriptionService } from '../services/subscriptionService';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showCancellationFlow, setShowCancellationFlow] = useState(false);
  const [cancellationStep, setCancellationStep] = useState('reason'); // 'reason', 'retention', 'confirmed'
  const [cancellationData, setCancellationData] = useState({
    reason: '',
    feedback: ''
  });
  const [retentionOffer, setRetentionOffer] = useState(null);
  const [processingCancellation, setProcessingCancellation] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    business_name: '',
    avatar_url: '',
    email: '',
    white_label_unlocked: false
  });

  useEffect(() => {
    if (user) {
      loadProfile();
      loadSubscriptionInfo();
      analytics.trackPageView('Profile Settings');
      verifyWhitelabelSession();
    }
  }, [user]);

  const verifyWhitelabelSession = async () => {
    const sessionId = searchParams.get('whitelabel_session');
    if (!sessionId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch('/api/stripe/verify-whitelabel-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ sessionId })
      });

      const result = await response.json();
      if (response.ok && result.success) {
        toast.success('White-label branding unlocked successfully!');
        await loadProfile();
        // Remove the query param from URL
        searchParams.delete('whitelabel_session');
        setSearchParams(searchParams, { replace: true });
      } else {
        toast.error(result.error || 'Failed to verify payment');
      }
    } catch (error) {
      console.error('Verify whitelabel session error:', error);
      toast.error('Failed to verify payment');
    }
  };

  const handleUnlockWhitelabel = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in again');
        return;
      }

      setLoading(true);
      const response = await fetch('/api/stripe/create-whitelabel-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        }
      });

      const result = await response.json();
      if (response.ok && result.url) {
        window.location.href = result.url;
      } else {
        toast.error(result.error || 'Failed to start checkout');
      }
    } catch (error) {
      console.error('Unlock whitelabel error:', error);
      toast.error('Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const info = await subscriptionService.getSubscriptionInfo(user.id);
      setSubscriptionInfo(info);
    } catch (error) {
      console.error('Failed to load subscription info:', error);
    }
  };

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      setProfile({
        full_name: data.full_name || '',
        business_name: data.business_name || '',
        avatar_url: data.avatar_url || '',
        email: user.email || '',
        white_label_unlocked: data.white_label_unlocked === true
      });
    } catch (error) {
      toast.error('Failed to load profile: ' + error.message);
      analytics.trackError(error, { context: 'load_profile' });
    }
  };

  const handleLogoRemove = async () => {
    if (!profile.avatar_url) return;
    try {
      setUploadingLogo(true);
      
      // Delete from R2 storage using the full URL (not just key)
      try {
        await r2Storage.deleteFile(profile.avatar_url);
        console.log('✅ Logo deleted from R2 storage');
      } catch (deleteError) {
        console.warn('⚠️ Failed to delete from R2 storage (continuing anyway):', deleteError);
      }
      
      // Update database
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);
      if (error) throw error;
      
      // Update local state
      setProfile(prev => ({ ...prev, avatar_url: '' }));
      setImageKey(Date.now());
      toast.success('Logo removed!');
      analytics.track('Logo Removed', { url: profile.avatar_url });
    } catch (error) {
      toast.error('Failed to remove logo: ' + error.message);
      analytics.trackError(error, { context: 'logo_removal' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (event) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image must be less than 2MB');
        return;
      }
      setUploadingLogo(true);
      const { url } = await r2Storage.uploadLogo(file);
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id);
      if (error) throw error;
      setProfile(prev => ({ ...prev, avatar_url: url }));
      setImageKey(Date.now());
      toast.success('Logo uploaded!');
      analytics.track('Logo Upload Completed');
    } catch (error) {
      toast.error('Failed to upload logo: ' + error.message);
      analytics.trackError(error, { context: 'logo_upload' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!profile.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profile.full_name,
          business_name: profile.business_name
        })
        .eq('id', user.id);
      if (error) throw error;
      toast.success('Profile updated!');
      analytics.track('Profile Update Completed');
    } catch (error) {
      toast.error('Failed to update profile: ' + error.message);
      analytics.trackError(error, { context: 'profile_update' });
    } finally {
      setLoading(false);
    }
  };

  // Cancellation flow handlers
  const handleCancellationStart = () => {
    setShowCancellationFlow(true);
    setCancellationStep('reason');
    analytics.track('Cancellation Flow Started');
  };

  const handleCancellationSubmit = async () => {
    if (!cancellationData.reason) {
      toast.error('Please select a reason for cancellation');
      return;
    }

    setProcessingCancellation(true);
    
    try {
      const result = await subscriptionService.requestCancellation(cancellationData);
      
      if (result.showRetentionOffer) {
        // Show retention offer
        setRetentionOffer(result.retentionOffer);
        setCancellationStep('retention');
        analytics.track('Retention Offer Shown', {
          reason: cancellationData.reason,
          offerType: result.retentionOffer.type
        });
      } else if (result.cancelled) {
        // Cancellation completed
        setCancellationStep('confirmed');
        toast.success(result.message);
        await loadSubscriptionInfo(); // Refresh subscription status
        analytics.track('Subscription Cancelled', {
          reason: cancellationData.reason,
          effectiveDate: result.effectiveDate
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process cancellation');
      analytics.trackError(error, { context: 'cancellation_request' });
    } finally {
      setProcessingCancellation(false);
    }
  };

  const handleRetentionAccept = async () => {
    setProcessingCancellation(true);
    
    try {
      const result = await subscriptionService.acceptRetentionOffer(
        retentionOffer.type,
        cancellationData.reason
      );
      
      if (result.retained) {
        toast.success(result.message);
        setShowCancellationFlow(false);
        await loadSubscriptionInfo(); // Refresh subscription status
        analytics.track('Retention Offer Accepted', {
          offerType: retentionOffer.type,
          originalReason: cancellationData.reason
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to apply retention offer');
      analytics.trackError(error, { context: 'retention_acceptance' });
    } finally {
      setProcessingCancellation(false);
    }
  };

  const handleRetentionDecline = async () => {
    setProcessingCancellation(true);
    
    try {
      const result = await subscriptionService.declineRetentionOffer(
        cancellationData.reason,
        cancellationData.feedback
      );
      
      if (result.cancelled) {
        setCancellationStep('confirmed');
        toast.success(result.message);
        await loadSubscriptionInfo(); // Refresh subscription status
        analytics.track('Retention Offer Declined', {
          offerType: retentionOffer.type,
          reason: cancellationData.reason,
          effectiveDate: result.effectiveDate
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription');
      analytics.trackError(error, { context: 'retention_decline' });
    } finally {
      setProcessingCancellation(false);
    }
  };

  const handleCancellationClose = () => {
    setShowCancellationFlow(false);
    setCancellationStep('reason');
    setCancellationData({ reason: '', feedback: '' });
    setRetentionOffer(null);
  };

  return (
    <div className="min-h-screen bg-white text-black font-jetbrains">
      {/* Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 border-b border-black">
        <div className="container max-w-7xl mx-auto px-4 h-16 flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-black hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-instrument text-xl">Back to Dashboard</span>
          </button>
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="container max-w-7xl mx-auto px-4 space-y-16">
          <h1 className="font-instrument text-5xl md:text-6xl tracking-tight">Profile Settings</h1>

          {/* Logo Section */}
          <section className="border border-black p-8 rounded-2xl">
            <h2 className="font-instrument text-3xl tracking-tight mb-6">Business Logo</h2>
            <div className="flex items-start space-x-8">
              <div className="w-28 h-28 rounded-2xl overflow-hidden border border-black">
                {profile.avatar_url ? (
                  <img
                    key={imageKey}
                    src={`${profile.avatar_url}?t=${imageKey}`}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-4">
                <label
                  htmlFor="logo-upload"
                  className="inline-flex items-center px-6 py-3 text-lg font-semibold border border-black rounded-lg hover:bg-black hover:text-white transition cursor-pointer"
                >
                  {uploadingLogo ? (
                    <>
                      <div className="spinner spinner-sm -ml-1 mr-2" aria-hidden="true"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Logo
                    </>
                  )}
                </label>
                <input id="logo-upload" type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                {profile.avatar_url && (
                  <button
                    onClick={handleLogoRemove}
                    disabled={uploadingLogo}
                    className="ml-4 px-6 py-3 text-lg font-semibold border border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition"
                  >
                    Remove
                  </button>
                )}
                <p className="text-sm text-gray-500">JPG, PNG, SVG • Max 2MB • Square recommended</p>
              </div>
            </div>
          </section>

          {/* Business Information */}
          <section className="border border-black p-8 rounded-2xl">
            <h2 className="font-instrument text-3xl tracking-tight mb-6">Business Information</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2">Business Name</label>
                  <input
                    type="text"
                    name="business_name"
                    value={profile.business_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="Your Business Name"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-2">Your Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    value={profile.full_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block font-semibold mb-2">Email Address</label>
                  <input
                    type="email"
                    value={profile.email}
                    className="w-full px-4 py-3 border border-black rounded-lg bg-gray-100 cursor-not-allowed"
                    disabled
                  />
                  <p className="text-sm text-gray-500 mt-1">Contact support to change</p>
                </div>
              </div>
              
              {/* Save Button - Inside Form */}
              <div className="flex justify-end mt-8">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </section>

          {/* Subscription Status */}
          <section className="border border-black p-8 rounded-2xl">
            <h2 className="font-instrument text-3xl tracking-tight mb-6">Account Status</h2>
            {subscriptionInfo ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold text-lg">Subscription Status</h3>
                    <p className="text-sm text-gray-600">Current account status and billing information</p>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      subscriptionInfo.displayStatus === 'Free Plan' ? 'bg-blue-100 text-blue-800' :
                      subscriptionInfo.displayStatus === 'Paid Plan' || subscriptionInfo.displayStatus === 'Paid Account' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {subscriptionInfo.displayStatus}
                    </div>
                    {subscriptionInfo.planType === 'free' && (
                      <p className="text-sm text-gray-600 mt-1">
                        Unlimited quotes — free forever
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500">Loading subscription status...</div>
            )}
          </section>

          {/* White-Label Branding Unlock */}
          <section className="border border-black p-8 rounded-2xl">
            <h2 className="font-instrument text-3xl tracking-tight mb-6">Branding</h2>
            {profile.white_label_unlocked ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-green-800 mb-1">White-Label Unlocked</h4>
                    <p className="text-green-700 text-sm">
                      "Powered by QuoteTier" branding has been removed from your client-facing quotes and PDFs.
                    </p>
                  </div>
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 whitespace-nowrap">
                    Active
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 border rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-lg">Remove QuoteTier Branding</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      One-time $29 unlock. Permanently removes the "Powered by QuoteTier" watermark from your client quote pages and PDFs.
                    </p>
                  </div>
                  <button
                    onClick={handleUnlockWhitelabel}
                    disabled={loading}
                    className="px-6 py-3 text-base font-semibold bg-black text-white rounded-lg hover:bg-gray-800 transition whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Loading...' : 'Unlock for $29'}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Subscription Management */}
          {subscriptionInfo && subscriptionInfo.planType === 'paid' && (
            <section className="border border-black p-8 rounded-2xl">
              <h2 className="font-instrument text-3xl tracking-tight mb-6">Subscription Management</h2>
              
              {!showCancellationFlow ? (
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">Manage Your Subscription</h3>
                        <p className="text-sm text-gray-600">
                          Need to make changes or cancel your subscription?
                        </p>
                      </div>
                      <button
                        onClick={handleCancellationStart}
                        className="px-6 py-3 text-lg font-semibold border border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition"
                      >
                        Cancel Subscription
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Cancellation Flow */}
                  <div className="border rounded-2xl p-6">
                    {cancellationStep === 'reason' && (
                      <div className="space-y-6">
                        <div className="text-center">
                          <h3 className="font-instrument text-2xl tracking-tight mb-2">
                            We're Sorry to See You Go
                          </h3>
                          <p className="text-gray-600">
                            Help us understand why you're cancelling so we can improve.
                          </p>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block font-semibold mb-3">Why are you cancelling?</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {subscriptionService.getCancellationReasons().map(reason => (
                                <label
                                  key={reason.value}
                                  className={`p-4 border rounded-lg cursor-pointer transition ${
                                    cancellationData.reason === reason.value
                                      ? 'border-black bg-gray-50'
                                      : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <input
                                      type="radio"
                                      name="cancellationReason"
                                      value={reason.value}
                                      checked={cancellationData.reason === reason.value}
                                      onChange={(e) => setCancellationData(prev => ({
                                        ...prev,
                                        reason: e.target.value
                                      }))}
                                      className="mt-1"
                                    />
                                    <div>
                                      <div className="font-semibold">{reason.label}</div>
                                      <div className="text-sm text-gray-600">{reason.description}</div>
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <label className="block font-semibold mb-2">
                              Additional feedback (optional)
                            </label>
                            <textarea
                              value={cancellationData.feedback}
                              onChange={(e) => setCancellationData(prev => ({
                                ...prev,
                                feedback: e.target.value
                              }))}
                              className="w-full px-4 py-3 border border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                              placeholder="Tell us more about your experience..."
                              rows={3}
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-between">
                          <button
                            onClick={handleCancellationClose}
                            className="px-6 py-3 text-lg font-semibold border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                          >
                            Keep Subscription
                          </button>
                          <button
                            onClick={handleCancellationSubmit}
                            disabled={!cancellationData.reason || processingCancellation}
                            className="px-6 py-3 text-lg font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                          >
                            {processingCancellation ? 'Processing...' : 'Continue'}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {cancellationStep === 'retention' && retentionOffer && (
                      <div className="space-y-6">
                        <div className="text-center">
                          <h3 className="font-instrument text-2xl tracking-tight mb-2">
                            Wait! We Have a Special Offer
                          </h3>
                          <p className="text-gray-600">
                            Before you go, here's something that might help.
                          </p>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl border">
                          <div className="text-center">
                            <h4 className="font-instrument text-xl font-semibold mb-2">
                              {retentionOffer.title}
                            </h4>
                            <p className="text-gray-700 mb-4">
                              {retentionOffer.description}
                            </p>
                            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full font-semibold">
                              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                              You'll Save: {retentionOffer.savings}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between">
                          <button
                            onClick={handleRetentionDecline}
                            disabled={processingCancellation}
                            className="px-6 py-3 text-lg font-semibold border border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            {processingCancellation ? 'Processing...' : 'No Thanks, Cancel Anyway'}
                          </button>
                          <button
                            onClick={handleRetentionAccept}
                            disabled={processingCancellation}
                            className="px-6 py-3 text-lg font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                          >
                            {processingCancellation ? 'Applying...' : subscriptionService.formatRetentionOffer(retentionOffer)?.buttonText || 'Accept Offer'}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {cancellationStep === 'confirmed' && (
                      <div className="space-y-6">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <h3 className="font-instrument text-2xl tracking-tight mb-2">
                            Subscription Cancelled
                          </h3>
                          <p className="text-gray-600">
                            Your subscription has been successfully cancelled. You'll continue to have access until the end of your current billing period.
                          </p>
                        </div>
                        
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <h4 className="font-semibold text-blue-800 mb-2">What happens next?</h4>
                          <ul className="text-blue-700 text-sm space-y-1">
                            <li>• You'll keep access until your current billing period ends</li>
                            <li>• No future charges will be made to your payment method</li>
                            <li>• Your data will be safely stored for 30 days after cancellation</li>
                            <li>• You can reactivate anytime by contacting support</li>
                          </ul>
                        </div>
                        
                        <div className="text-center">
                          <button
                            onClick={handleCancellationClose}
                            className="px-6 py-3 text-lg font-semibold bg-black text-white rounded-lg hover:bg-gray-800 transition"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

        </div>
      </main>
    </div>
  );
};

export default ProfileSettings;
